/**
 * Las capas de la Bitácora: UNA sola definición, sin React.
 *
 * Vivían adentro de `MemoryBitacoraView`, privadas. Salieron acá porque hay DOS superficies que
 * tienen que hablar del MISMO universo de eventos: la Bitácora, que los **lista**, y el pulso de la
 * tarjeta «Historial» del acto Memoria (`MemoryDuo` + `HistorySeismograph`), que los **cuenta**. Con
 * la definición de un lado y el otro adivinando, el número y la lista divergen sin que nadie se
 * entere — y ya pasó dos veces seguidas, una en cada dirección:
 *
 * 1. el pulso se pedía SIN `categories`, y en ese caso el backend cuenta el complemento exacto de la
 *    capa de plataformas (`_scoped()` cae a `created_after(...).excluding_platform_changes()`): una
 *    barra de 5 abría un día con 0 filas;
 * 2. al corregirlo se pasó a pedir sólo `PLATFORM_CHANGE`, pero la Bitácora abre con SEIS capas
 *    prendidas (todas menos `alerts`): una barra de 0 abría un día con 20 filas.
 *
 * Un número y la lista que lo acompaña se definen una vez. De acá salen las dos cosas que hacen
 * falta para eso: el catálogo de capas y la resolución de cuáles están activas (deep-link → la
 * preferencia guardada → los defaults).
 *
 * Es lógica pura: se importa desde cualquier lado sin arrastrar la Bitácora entera (que trae
 * APIClient, las entradas del timeline y el cache de métricas personalizadas).
 */

// Cada chip agrupa una o más categorías del backend y se activa/desactiva localmente (persistido en
// localStorage, global — no por cliente). Las Alertas arrancan APAGADAS: son la capa más ruidosa y
// su lugar natural es el Monitoreo; el conteo del chip deja ver que están ahí.
//
// `group` dice DÓNDE pasó el cambio, y los chips se dibujan agrupados por él (card 2BZI0Us8): en la
// pasada del 23/09 «Presupuesto» se leyó como un cambio de presupuesto hecho en Meta, cuando es lo
// que alguien tocó en Dashbo. El rótulo solo no alcanzaba; el grupo es lo que lo desambigua.
export const LAYER_GROUP_DASHBO = 'dashbo';
export const LAYER_GROUP_PLATFORM = 'platform';
export const LAYER_GROUP_CONTEXT = 'context';

export const LAYER_GROUPS = [LAYER_GROUP_DASHBO, LAYER_GROUP_PLATFORM, LAYER_GROUP_CONTEXT];

// El orden de la lista NO es el de pantalla (ése lo dan los grupos): es el del CSV `categories` que
// viaja al backend, y el pulso del acto Memoria lo compara byte a byte. No reordenar.
export const LAYER_DEFS = [
    {key: 'platform', group: LAYER_GROUP_PLATFORM, categories: ['PLATFORM_CHANGE'], defaultOn: true, requiresPlatformModule: true},
    {key: 'memory', group: LAYER_GROUP_CONTEXT, categories: ['EXTERNAL', 'MEMORY_OPS'], defaultOn: true},
    {key: 'budget', group: LAYER_GROUP_DASHBO, categories: ['BUDGET'], defaultOn: true},
    {key: 'configuration', group: LAYER_GROUP_DASHBO, categories: ['CONFIGURATION', 'STATUS', 'ACCOUNTS', 'INTEGRATIONS', 'REPORTS', 'AGENT'], defaultOn: true},
    {key: 'comments', group: LAYER_GROUP_DASHBO, categories: ['COMMENT'], defaultOn: true},
    {key: 'team', group: LAYER_GROUP_DASHBO, categories: ['TEAM'], defaultOn: true},
    {key: 'alerts', group: LAYER_GROUP_CONTEXT, categories: ['ALERTS'], defaultOn: false},
];

export const LAYERS_STORAGE_KEY = 'dashbo_bitacora_layers';

export function loadStoredLayers() {
    try {
        const raw = window.localStorage.getItem(LAYERS_STORAGE_KEY);
        if (raw) {
            const keys = JSON.parse(raw);
            if (Array.isArray(keys)) return new Set(keys);
        }
    } catch (e) { /* localStorage roto/lleno: caemos al default */ }
    return new Set(LAYER_DEFS.filter(l => l.defaultOn).map(l => l.key));
}

export function storeLayers(activeSet) {
    try {
        window.localStorage.setItem(LAYERS_STORAGE_KEY, JSON.stringify([...activeSet]));
    } catch (e) { /* best-effort */ }
}

// Deep-link: la Bitácora acepta una "búsqueda armada" por URL (?layers=alerts,budget&date_from=...
// &date_to=...). Es el contrato con el que otras superficies (el Listado de Clientes, el Panel, la
// campanita, el Command Center) la abren ya filtrada. Los filtros del deep-link valen para ESA
// visita: no pisan la preferencia de capas guardada del usuario.
export function readDeepLinkParams() {
    try {
        const params = new URLSearchParams(window.location.search);
        const validKeys = new Set(LAYER_DEFS.map(l => l.key));
        const layers = (params.get('layers') || '').split(',').map(k => k.trim()).filter(k => validKeys.has(k));
        const dateFrom = params.get('date_from') || '';
        const dateTo = params.get('date_to') || '';
        if (!layers.length && !dateFrom && !dateTo) return null;
        return {layers, dateFrom, dateTo};
    } catch (e) {
        return null;
    }
}

export function clearDeepLinkParamsFromUrl() {
    try {
        const url = new URL(window.location.href);
        ['layers', 'date_from', 'date_to'].forEach(p => url.searchParams.delete(p));
        window.history.replaceState(null, '', url.pathname + (url.search || '') + url.hash);
    } catch (e) { /* best-effort */ }
}

/** Las categorías del backend (parámetro `categories`) que corresponden a un set de capas. */
export function categoriesOfLayers(activeSet) {
    return LAYER_DEFS
        .filter(l => activeSet.has(l.key))
        .reduce((acc, l) => acc.concat(l.categories), []);
}

/**
 * Las capas activas AHORA, como categorías del backend — la misma resolución que hace el
 * constructor de la Bitácora (deep-link si lo hay; si no, la preferencia guardada; si no, los
 * defaults), expuesta como función pura.
 *
 * Existe para que quien la aloje pueda pedir el MISMO universo **antes** de montarla. El caso vivo
 * es el pulso del acto Memoria: su número tiene que estar en la tarjeta antes de que el usuario
 * abra la bandeja, así que no puede esperar a que la Bitácora se monte y avise. Después el
 * componente publica cada cambio de capas (`onActiveCategoriesChange`) y los dos siguen juntos.
 *
 * No toca la URL ni el localStorage.
 */
export function activeBitacoraCategories() {
    const deepLink = readDeepLinkParams();
    const active = (deepLink && deepLink.layers.length)
        ? new Set(deepLink.layers)
        : loadStoredLayers();
    return categoriesOfLayers(active);
}

/**
 * Las categorías que el backend DESCARTA si la agencia no tiene el módulo de Memoria de cambios en
 * plataformas (`MODULE_PLATFORM_CHANGE_MEMORY`). Sale del mismo catálogo que el chip que se esconde
 * sin el módulo, así que no hay una segunda lista que se pueda desincronizar.
 */
export function moduleGatedCategories() {
    return LAYER_DEFS
        .filter(l => l.requiresPlatformModule)
        .reduce((acc, l) => acc.concat(l.categories), []);
}
